import React, { type ButtonHTMLAttributes, type ReactNode } from "react";

import { IconLoader } from "@tabler/icons-react";

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** The visual style of the button: 'primary' or 'secondary' */
  variant?: "primary" | "secondary" | "danger" | "outline";
  /** Whether the button is loading */
  isLoading?: boolean;
  /** Icon to display inside the button (optional) */
  icon?: ReactNode;
  /** Icon position: 'left' or 'right' */
  iconPosition?: "left" | "right";
}

export const Button: React.FC<ButtonProps> = ({
  children,
  variant = "primary",
  isLoading = false,
  icon,
  iconPosition = "left",
  disabled,
  className = "",
  ...rest
}) => {
  // Define base styles for the button
  const baseStyles =
    "inline-flex cursor-pointer items-center justify-center px-2 md:px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2 dark:focus:ring-offset-zinc-800 focus:ring-green-500";

  // Define styles for the primary button
  const primaryStyles =
    "bg-green-500 text-white hover:bg-green-600 active:bg-green-700 focus:ring-green-500 disabled:bg-green-400 disabled:cursor-not-allowed dark:bg-green-400 dark:hover:bg-green-500 dark:active:bg-green-600 dark:disabled:bg-green-300";

  // Define styles for the secondary button
  const secondaryStyles =
    "bg-green-100 text-green-500 hover:bg-green-200 active:bg-green-300 focus:ring-green-200 disabled:bg-green-100 disabled:text-green-300 disabled:cursor-not-allowed dark:bg-green-200 dark:text-green-400 dark:hover:bg-green-300 dark:active:bg-green-400 dark:disabled:bg-green-100";

  const dangerStyles =
    "bg-red-500 text-white hover:bg-red-600 active:bg-red-700 focus:ring-red-500 disabled:bg-red-400 disabled:cursor-not-allowed dark:bg-red-700 dark:hover:bg-red-600 dark:active:bg-red-700 dark:disabled:bg-red-950";

  const outlineStyles =
    "bg-transparent text-green-500 hover:bg-green-500/10 active:bg-green-500/20 focus:ring-green-200 disabled:bg-green-100 disabled:text-green-300 disabled:cursor-not-allowed  dark:text-green-300 dark:disabled:bg-green-100";

  // Combine styles based on the variant
  const variantStyles =
    variant === "primary"
      ? primaryStyles
      : variant === "secondary"
      ? secondaryStyles
      : variant === "danger"
      ? dangerStyles
      : outlineStyles;

  return (
    <button
      {...rest}
      className={`${baseStyles} ${variantStyles} ${className}`}
      disabled={disabled || isLoading}
    >
      {/* Add loader or icon */}
      {isLoading ? (
        <IconLoader className="w-5 h-5 animate-spin mr-2" />
      ) : icon && iconPosition === "left" ? (
        <span className="mr-3">{icon}</span>
      ) : null}

      {/* Button label */}
      {children}

      {/* Add right-aligned icon */}
      {!isLoading && icon && iconPosition === "right" && (
        <span className="ml-3">{icon}</span>
      )}
    </button>
  );
};
